import { StartingSetupApplied, StartingSetupCard } from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// The starting setup file on the Go-live checklist (APP-118): upload → preview (nothing saved) →
// apply as one audited batch, Check again after a sync, and Export at any time.

const BASE = '/api/v1/admin/starting-setup';
const Id = z.uuid();
const enc = (id: string) => encodeURIComponent(Id.parse(id));

/** Under Admin's key, so a refresh of everything Admin refreshes the card too. */
export const startingSetupKey = ['admin', 'starting-setup'] as const;

export const startingSetupQuery = queryOptions({
  queryKey: startingSetupKey,
  queryFn: ({ signal }) => request(BASE, { schema: StartingSetupCard, signal }),
  staleTime: 30_000,
  retry: false,
});

/** Sends the file and gets back its preview; nothing it changes is saved yet. */
export function uploadStartingSetup(file: File): Promise<StartingSetupCard> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request(BASE, { method: 'POST', body: form, schema: StartingSetupCard });
}

/** Check again: the kept file's preview, worked out against the data as it is now. */
export function checkStartingSetup(id: string): Promise<StartingSetupCard> {
  return request(`${BASE}/${enc(id)}/check`, { method: 'POST', schema: StartingSetupCard });
}

/**
 * APP-205: the letter pack's .zip beside the file. It must be the one the file pins; the preview
 * comes back worked out with the pack's letters in it.
 */
export function uploadStartingSetupLetterPack(id: string, file: File): Promise<StartingSetupCard> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request(`${BASE}/${enc(id)}/letter-pack`, {
    method: 'POST',
    body: form,
    schema: StartingSetupCard,
  });
}

/** Saves every change as one audited batch, if the preview is still the one the person saw. */
export function applyStartingSetup(
  id: string,
  body: { version: number; reason: string },
): Promise<StartingSetupApplied> {
  return request(`${BASE}/${enc(id)}/apply`, {
    method: 'POST',
    body,
    schema: StartingSetupApplied,
  });
}

/** The blank file, with this environment's sector and pool ids (a plain link). */
export const blankStartingSetupHref = `${BASE}/blank`;

/** This environment's setup as a file (a plain link; the server records the export). */
export const exportStartingSetupHref = `${BASE}/export`;
